import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  FolderSimple, FileArrowUp, Gauge, GearSix, Lightning, XCircle, CheckCircle, MagnifyingGlass, UserPlus, FolderOpen,
  Kanban, MapPinLine, Bell, CaretRight, ListChecks, Phone, X,
} from "@phosphor-icons/react";
import { api, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { FieldPanel } from "../components/FieldPanel";
import { DelayedFiles } from "../components/DelayedFiles";

const CARDS = [
  { key: "pending_handover", label: "File handover", color: "#EA580C", icon: FolderSimple, statuses: ["created", "forwarded_to_manager", "with_manager", "forwarded_to_field"] },
  { key: "pending_field_discom", label: "With field / Discom", color: "#2563EB", icon: FileArrowUp, statuses: ["with_field_officer", "submitted_to_discom", "resolved"] },
  { key: "pending_meter_install", label: "Meter install", color: "#7C3AED", icon: Gauge, statuses: ["indent_generated"] },
  { key: "pending_configuration", label: "Meter config", color: "#D97706", icon: GearSix, statuses: ["meter_installed"] },
  { key: "pending_activation", label: "Activation", color: "#0891B2", icon: Lightning, statuses: ["meter_configured"] },
  { key: "rejected", label: "Rejected", color: "#DC2626", icon: XCircle, statuses: ["rejected"] },
  { key: "completed", label: "Completed", color: "#0D9488", icon: CheckCircle, statuses: ["completed"] },
];

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

const Card = ({ title, action, children, className = "", testid }) => (
  <section className={`rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04)] ${className}`} data-testid={testid}>
    {title && <div className="flex items-center justify-between px-4 sm:px-5 pt-4"><h3 className="font-heading font-bold text-[15px] text-slate-900">{title}</h3>{action}</div>}
    {children}
  </section>
);

function SearchBox() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!q.trim()) { setRes(null); return undefined; }
    const t = setTimeout(() => api.get("/customers", { params: { search: q.trim() } }).then((r) => setRes(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <MagnifyingGlass size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input data-testid="dashboard-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, consumer or registration no."
        className="w-full h-12 pl-10 pr-10 rounded-xl bg-white text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400 shadow-lg shadow-black/10" />
      {q && <button type="button" aria-label="Clear search" data-testid="dashboard-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><X size={16} /></button>}
      {res !== null && (
        <div data-testid="dashboard-search-results" className="absolute z-30 mt-2 w-full rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 max-h-80 overflow-y-auto">
          {res.length === 0 ? <div className="p-5 text-sm text-slate-500 text-center" data-testid="dashboard-search-empty">No matching customer</div> : res.map((r) => (
            <Link key={r.id} to={`/customers/${r.id}`} data-testid={`search-result-${r.id}`} className="flex items-center justify-between gap-2 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="min-w-0"><div className="font-semibold text-sm text-slate-900 truncate">{r.name}</div><div className="text-xs text-slate-500 truncate font-mono">{r.mobile} · {r.consumer_no || "—"}</div></div>
              <StatusBadge status={r.status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Hero({ user, stats }) {
  const pct = stats && stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;
  return (
    <div className="relative overflow-hidden rounded-3xl bg-ink-950 text-white px-5 py-6 sm:px-8 sm:py-8" data-testid="dashboard-hero">
      <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-brand-500/25 blur-3xl" />
      <div className="absolute -bottom-28 left-10 w-64 h-64 rounded-full bg-sky-500/10 blur-3xl" />
      <div className="relative flex flex-col lg:flex-row lg:items-end gap-6">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-brand-300">{ROLE_LABELS[user.role]}</div>
          <h1 className="text-2xl sm:text-4xl font-heading font-extrabold tracking-tight mt-1.5">{greeting()}, {user.name.split(" ")[0]}</h1>
          <p className="text-sm text-slate-400 mt-1">Here is what needs your attention today.</p>
          <div className="mt-5 max-w-xl"><SearchBox /></div>
        </div>
        {stats && user.role !== "technician" && (
          <div className="flex gap-3 lg:gap-4">
            <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 px-4 py-3 min-w-[110px]"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total files</div><div className="font-heading text-3xl font-extrabold" data-testid="hero-total">{stats.total}</div></div>
            <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 px-4 py-3 min-w-[110px]"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Completed</div><div className="font-heading text-3xl font-extrabold text-brand-300" data-testid="hero-completed">{pct}%</div></div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuickActions({ user }) {
  const perms = user.permissions || [];
  const items = [
    perms.includes("create_customer") && { to: "/customers/new", label: "New file", icon: UserPlus, tone: "bg-brand-500 text-white" },
    { to: "/customers", label: "Customers", icon: FolderOpen, tone: "bg-sky-50 text-sky-700" },
    { to: "/work", label: "Work hub", icon: Kanban, tone: "bg-violet-50 text-violet-700" },
    user.role !== "field_officer" && { to: "/inspections", label: "Sites", icon: MapPinLine, tone: "bg-orange-50 text-orange-700" },
    { to: "/notifications", label: "Alerts", icon: Bell, tone: "bg-rose-50 text-rose-600" },
  ].filter(Boolean);
  return (
    <div className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-none -mx-1 px-1" data-testid="quick-actions">
      {items.map((a) => (
        <Link key={a.to} to={a.to} data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, "-")}`} aria-label={a.label}
          className="tap group flex flex-col items-center gap-1.5 min-w-[64px]">
          <span className={`w-14 h-14 rounded-2xl flex items-center justify-center ring-1 ring-black/[0.04] shadow-sm transition-transform group-hover:-translate-y-0.5 group-active:scale-95 ${a.tone}`}><a.icon size={24} weight="duotone" /></span>
          <span className="text-[11px] font-semibold text-slate-600">{a.label}</span>
        </Link>
      ))}
    </div>
  );
}

function TodayWork({ tasks, isTech }) {
  const top = tasks.slice(0, 5);
  return (
    <Card title={isTech ? "Systems to activate" : "Today's work"} testid="today-work"
      action={<span className="text-xs font-mono font-bold rounded-full bg-brand-50 text-brand-700 px-2.5 py-1" data-testid="today-work-count">{tasks.length} pending</span>}>
      {top.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="empty-today"><CheckCircle size={30} weight="duotone" className="mx-auto mb-2 text-brand-500" />You are all caught up.</div>
      ) : (
        <div className="mt-2 divide-y divide-slate-100">
          {top.map((t) => (
            <Link key={t.id} to={`/customers/${t.id}`} data-testid={`today-row-${t.id}`} className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50 transition-colors">
              <span className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 font-heading font-bold text-sm flex items-center justify-center shrink-0">{(t.name || "?").slice(0, 1)}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-slate-900 truncate">{t.name}</div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1">{t.mobile && <><Phone size={11} /> <span className="font-mono">{t.mobile}</span></>}</div>
              </div>
              <StatusBadge status={t.status} />
              <CaretRight size={16} className="text-slate-300 shrink-0" />
            </Link>
          ))}
        </div>
      )}
      {tasks.length > 5 && <Link to="/customers" className="block text-center text-sm font-semibold text-brand-700 py-3 border-t border-slate-100 hover:bg-slate-50 rounded-b-2xl" data-testid="today-view-all">View all {tasks.length}</Link>}
    </Card>
  );
}

function Pipeline({ stats }) {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2.5" data-testid="pipeline-cards">
      {CARDS.map((c) => (
        <button key={c.key} type="button" data-testid={`stat-${c.key}`} onClick={() => navigate(`/customers?status=${c.statuses.join(",")}`)}
          className="tap group rounded-2xl bg-white border border-slate-200/80 p-3.5 text-left hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-[box-shadow,transform]">
          <div className="flex items-center justify-between">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${c.color}14`, color: c.color }}><c.icon size={19} weight="duotone" /></span>
            <span className="font-heading text-2xl font-extrabold text-slate-900">{stats[c.key]}</span>
          </div>
          <div className="text-[11.5px] font-semibold text-slate-500 mt-2.5 leading-tight">{c.label}</div>
          <div className="h-1 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${stats.total ? Math.max(4, (stats[c.key] / stats.total) * 100) : 0}%`, backgroundColor: c.color }} /></div>
        </button>
      ))}
    </div>
  );
}

function Charts({ stats }) {
  const pie = CARDS.map((c) => ({ name: c.label, value: stats[c.key], fill: c.color })).filter((d) => d.value > 0);
  const week = (stats.last7 || []).map((d) => ({ day: new Date(d.date).toLocaleDateString("en-IN", { weekday: "short" }), count: d.count }));
  const weekTotal = week.reduce((a, d) => a + d.count, 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-4">
      <Card title="Status mix" testid="chart-status">
        <div className="flex items-center gap-4 px-4 sm:px-5 pb-4">
          <div className="relative w-36 h-36 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={44} outerRadius={64} paddingAngle={2} stroke="none">{pie.map((d, i) => <Cell key={i} fill={d.fill} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><span className="font-heading text-2xl font-extrabold">{stats.total}</span><span className="text-[10px] text-slate-500 font-semibold">files</span></div>
          </div>
          <div className="flex-1 space-y-1.5 min-w-0">
            {pie.map((d) => <div key={d.name} className="flex items-center gap-2 text-xs"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.fill }} /><span className="text-slate-600 truncate flex-1">{d.name}</span><span className="font-mono font-bold">{d.value}</span></div>)}
          </div>
        </div>
      </Card>
      <Card title="New files · last 7 days" testid="chart-week" action={<span className="text-xs font-mono font-bold text-slate-500">{weekTotal} total</span>}>
        <div className="h-40 px-2 pb-3 pt-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={week} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip cursor={{ fill: "#F1F5F9" }} />
              <Bar dataKey="count" name="New files" fill="#0D9488" radius={[6, 6, 6, 6]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const isTech = user.role === "technician";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/tasks").then((r) => setTasks(r.data)).catch(() => {});
  }, []);
  const showStats = useMemo(() => stats && !isTech, [stats, isTech]);

  return (
    <div className="max-w-7xl mx-auto space-y-5 sm:space-y-6 stagger-in" data-testid="dashboard-page">
      <Hero user={user} stats={stats} />
      <QuickActions user={user} />
      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-4 sm:gap-5">
        <div className="space-y-4 sm:space-y-5">
          <TodayWork tasks={tasks} isTech={isTech} />
          {["super_admin", "manager"].includes(user.role) && <DelayedFiles canEdit={user.role === "super_admin"} />}
        </div>
        {showStats && stats.total > 0 ? <Charts stats={stats} /> : null}
      </div>
      {showStats && (
        <div className="space-y-3">
          <h2 className="font-heading font-bold text-[15px] text-slate-900 flex items-center gap-2"><ListChecks size={18} className="text-brand-600" /> Pipeline</h2>
          <Pipeline stats={stats} />
        </div>
      )}
      <div className="space-y-3">
        <h2 className="font-heading font-bold text-[15px] text-slate-900">Field work</h2>
        <FieldPanel tasks={tasks} />
      </div>
    </div>
  );
}
